/**
 * Recorder.ts - Generic Action Recorder
 *
 * Records player commands into a timeline with relative timestamps.
 * Exports the recording as a downloadable JSON file.
 */

import type { ICommand } from './Command';

export interface DemoData<TCmd extends ICommand = ICommand> {
  version: string;
  gameName: string;
  duration: number;
  commands: TCmd[];
}

type StatusListener = (recording: boolean) => void;

export class Recorder<TCmd extends ICommand = ICommand> {
  private _recording = false;
  private _commands: TCmd[] = [];
  private _startTime = 0;
  private _statusListeners: Set<StatusListener> = new Set();
  private _gameName: string;

  constructor(gameName = 'template') {
    this._gameName = gameName;
  }

  onStatusChange(cb: StatusListener): () => void {
    this._statusListeners.add(cb);
    return () => this._statusListeners.delete(cb);
  }

  private _notifyStatus(): void {
    this._statusListeners.forEach((cb) => cb(this._recording));
  }

  /** Start recording. Clears any previous data. */
  start(): void {
    this._commands = [];
    this._startTime = Date.now();
    this._recording = true;
    this._notifyStatus();
    console.log('[Recorder] Recording started');
  }

  /** Stop recording. */
  stop(): void {
    this._recording = false;
    this._notifyStatus();
    console.log('[Recorder] Recording stopped. Commands:', this._commands.length);
  }

  /** Record a command. Converts absolute timestamp to relative. */
  record(cmd: TCmd): TCmd {
    const relativeTimestamp = Date.now() - this._startTime;
    const recorded = {
      ...cmd,
      timestamp: this._recording ? relativeTimestamp : cmd.timestamp,
    };
    if (this._recording) {
      this._commands.push(recorded);
    }
    return recorded;
  }

  /** Check if currently recording */
  isRecording(): boolean {
    return this._recording;
  }

  /** Get the recorded data as a JSON-serializable object */
  getData(): DemoData<TCmd> {
    const duration =
      this._commands.length > 0
        ? this._commands[this._commands.length - 1].timestamp
        : 0;

    return {
      version: '1.0',
      gameName: this._gameName,
      duration,
      commands: this._commands.slice(),
    };
  }

  /** Export recording as a JSON file download */
  exportJSON(): void {
    const data = this.getData();

    if (data.commands.length === 0) {
      console.warn('[Recorder] No commands recorded');
      return;
    }

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download =
      'recording-' +
      new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-') +
      '.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('[Recorder] Exported', data.commands.length, 'commands');
  }
}
